import get from 'lodash-es/get';
import { v4 as uuidv4 } from 'uuid';
import { GwDatesWrapper } from '../../../components/_internal_date_/gwDatesWrapper';
import { editCopyByReplaceString } from '../../../components/utilities/copyFunctions';
import {
  CUSTOM_PURCHASE_LOCATIONS,
  DATE_FORMATS,
  DAY_PASSES_ROOM_TYPE,
  EVENT_CATEGORIES,
  EVENT_PARAMS,
  GTM_EVENT,
  PRODUCT_SCOPES,
  RECOMMENDED_FOR_YOU,
  RESERVATION_TYPE,
  SEARCH_TYPES
} from '../../../utilities/constants';
import { getLodgeDataByCode } from '../../../utilities/lodgeData';
import { getReservationCurrency } from '../../../utilities/reservationsUtils';
import { getSpecificItem, SessionStorageKeys } from '../../../utilities/storageUtils';
import { getPackageCategory, getPackageQuantity, getPkgType, isRecommendedPackage } from './analyticsUtils';
import * as COPY from './utilities/copy';

export const hashEmail = async email => {
  // encode as UTF-8
  const msgBuffer = new TextEncoder().encode(email.toLowerCase());
  // hash the message
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  // convert ArrayBuffer to Array
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  // convert bytes to hex string
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const applyAgentProperty = (returnObj, upsellRecommendationData) => {
  const { analyticalGroup, addons, content } = upsellRecommendationData ?? {};

  if (analyticalGroup === '') {
    returnObj.agent = 'GW';
    return;
  }

  if (analyticalGroup && !addons && content?.packageCode === returnObj.id) {
    returnObj.agent = analyticalGroup;
    return;
  }

  const packagesZS =
    upsellRecommendationData &&
    (Object.hasOwn(upsellRecommendationData, 'data') || Object.hasOwn(upsellRecommendationData, 'addons'))
      ? Object.keys(addons)
      : null;

  if (packagesZS && packagesZS.includes(returnObj.id)) {
    returnObj.agent = analyticalGroup;
  } else {
    returnObj.agent = 'GW';
  }
};

const agentHandoffAttribution = ({ handoffCartId, appliedViaPromoBanner }) => {
  if (handoffCartId) return { searchType: SEARCH_TYPES.aiPlanner, cartId: handoffCartId };

  return {
    searchType: appliedViaPromoBanner ? SEARCH_TYPES.promoSearch : SEARCH_TYPES.reservation,
    cartId: ''
  };
};

// Full enhanced E-Commerce Object
export const buildPurchaseEnhancedECommerceObject = async (
  {
    reservation,
    products,
    revenue,
    tax,
    shipping,
    voyagerPointsDiscount,
    offersPointsDiscount,
    dayType,
    UUID,
    upsellRecommendationData,
    seasonPassCode = '',
    couponCode = '',
    handoffCartId = '',
    appliedViaPromoBanner = false
  },
  eventCategory = EVENT_CATEGORIES.GTM.transaction,
  productScope = PRODUCT_SCOPES.suiteCheckout
) => {
  const uniquePackages = [...new Map(products.map(m => [m.id, m])).values()];
  uniquePackages.forEach(unique => {
    unique.dimension63 = reservation.payment.description;
  });

  const { searchType, cartId } = agentHandoffAttribution({ handoffCartId, appliedViaPromoBanner });

  let returnObj = {
    event: eventCategory,
    resortLocation: getLodgeDataByCode(reservation.lodge.code).url, //custom dimension for resort location that we can easily filter EE events by,
    purchaseLocation: productScope, //custom dimension for transaction location
    email_hashed: reservation?.contactInfo?.email ? await hashEmail(reservation?.contactInfo?.email) : '',
    searchType: searchType,
    cartId: cartId,
    ecommerce: {
      currencyCode: getReservationCurrency(reservation.lodge.code),
      purchase: {
        actionField: {
          id: UUID ? UUID : uuidv4(),
          confirmationNumber: reservation?.externalId ?? reservation?.id,
          affiliation: GTM_EVENT.LABEL.greatWolfLowerCase,
          revenue: revenue,
          shipping: shipping ?? 0,
          tax: tax,
          coupon:
            couponCode ||
            (reservation?.reservationType?.includes(RESERVATION_TYPE.SEASONPASS.toUpperCase())
              ? seasonPassCode || reservation?.rateCode || ''
              : reservation?.rateCode ?? ''),
          voyagerPointsDiscount: voyagerPointsDiscount ?? 0,
          offersPointsDiscount: offersPointsDiscount ?? 0,
          SIP: dayType
        },
        products: uniquePackages
      }
    }
  };
  if (upsellRecommendationData) {
    const hasAddOns = upsellRecommendationData?.addons && Object.keys(upsellRecommendationData?.addons)?.length > 0;
    const packagesZS = hasAddOns && Object.keys(upsellRecommendationData.addons);
    const ZScase = upsellRecommendationData?.analyticalGroup;
    const reservationHasZScase = uniquePackages?.some(unq => unq.agent === ZScase);
    hasAddOns && (reservationHasZScase || packagesZS?.includes(returnObj.id))
      ? (returnObj.ecommerce.purchase.actionField.agent = ZScase)
      : (returnObj.ecommerce.purchase.actionField.agent = 'GW');
  }
  return returnObj;
};

const getVariantForEcommerce = (isDaypass, isSeasonpass, suiteCategory) => {
  if (isDaypass && !isSeasonpass) return RESERVATION_TYPE.DAYPASS;
  if (isSeasonpass) return RESERVATION_TYPE.SEASONPASS;
  return suiteCategory; // standard, themed, premium, condo, cabin
};

const getQuantityForEcommerce = (isDaypass, isSeasonpass, guests) => {
  if (isDaypass || isSeasonpass) return guests.quantity;
  return 1;
};

// Enhanced E-Commerce experience for suite
export const buildSuitePurchaseEnhancedECommerceObject = (
  reservation,
  productScope = PRODUCT_SCOPES.suiteCheckout,
  maxOccupancy,
  suiteCategory,
  upsellRecommendationData,
  seasonPassCode = '',
  couponCode = ''
) => {
  const checkInDate = get(reservation, 'arrival', '');
  const checkoutDate = get(reservation, 'departure', '');
  const isDayPass = reservation.reservationType.includes('PASS');
  const isSeasonpass = reservation.reservationType.includes('SEASONPASS');
  const numberOfNights = GwDatesWrapper.diff(checkoutDate, checkInDate, 'days');
  const isDayPassOrSuite = isDayPass || isSeasonpass ? RESERVATION_TYPE.DAYPASS : RESERVATION_TYPE.SUITE;

  let returnObj = {
    name: isSeasonpass ? reservation.seasonPass?.name : reservation.product.name,
    id: reservation.reservationType,
    price: isDayPass
      ? (reservation.product.totalPrice / reservation.guests.quantity).toFixed(2)
      : reservation.product.totalPrice.toFixed(2),
    brand: 'great wolf',
    category: isDayPassOrSuite,
    variant: getVariantForEcommerce(isDayPass, isSeasonpass, suiteCategory),
    quantity: getQuantityForEcommerce(isDayPass, isSeasonpass, reservation.guests),
    coupon: couponCode || (isSeasonpass ? seasonPassCode || reservation?.rateCode || '' : reservation?.rateCode ?? ''),
    dimension29: getLodgeDataByCode(reservation.lodge.code).url, // product scope dimension for resort
    dimension30: isDayPassOrSuite, // product scope dimension for category
    dimension31: numberOfNights, // product scope dimension for number of nights
    dimension32: reservation.guests.adults, // product scope dimension for number of adults
    dimension33: reservation.guests.children.length ?? 0, // product scope dimension for number of kids
    dimension34: productScope, // product scope dimension for add to cart location (pre vs post transaction)
    dimension35: isSeasonpass
      ? GwDatesWrapper.format(reservation.seasonPass.startDate, DATE_FORMATS.default)
      : GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
    dimension36: isSeasonpass
      ? GwDatesWrapper.format(reservation.seasonPass.endDate, DATE_FORMATS.default)
      : GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    dimension61: maxOccupancy ?? null, // product scope dimension for Maximum Suite Occupancy
    dimension62: reservation.guests.children ?? [] // product scope dimension for Kids Ages
  };
  if (upsellRecommendationData?.analyticalGroup !== undefined || isSeasonpass || isDayPass) {
    applyAgentProperty(returnObj, upsellRecommendationData);
  }
  return returnObj;
};

// New enhances E-Commerce experience for packages
export const buildPackagesPurchaseEnhancedECommerceObject = (
  {
    addedPackagesCodes,
    packages,
    groupedAddedPackages,
    reservation,
    packagesContent,
    formatedOffers = [],
    maxOccupancy,
    upsellRecommendationData,
    couponCode = ''
  },
  productScope = PRODUCT_SCOPES.postPurchase
) => {
  const checkInDate = get(reservation, 'arrival', '');
  const checkoutDate = get(reservation, 'departure', '');
  const numberOfNights = GwDatesWrapper.diff(checkoutDate, checkInDate, 'days');

  const addedPackagesFormat = addedPackagesCodes.reduce((acc, packageCode) => {
    const packagesByCode = packages.reduce((keysAcc, currentPackage) => {
      keysAcc[currentPackage?.code || currentPackage?.packageCode] = currentPackage;
      return keysAcc;
    }, {});

    const pkgType = getPkgType(reservation?.addOns || packagesContent, packageCode);

    let returnObj = {
      name:
        packagesByCode[packageCode]?.name ||
        packagesByCode[packageCode]?.title ||
        packagesByCode[packageCode]?.description,
      id: packageCode,
      price: groupedAddedPackages[packageCode]?.reduce((revenueAcc, packageItem) => {
        return revenueAcc + (packageItem?.unitPrice ?? packageItem?.unitAmount);
      }, 0),
      brand: GTM_EVENT.LABEL.greatWolfLowerCase,
      category: 'packages',
      variant: pkgType,
      quantity: packageCode?.includes('PARKING') ? numberOfNights : packagesByCode[packageCode]?.quantity,
      coupon: couponCode || (reservation?.rateCode ?? reservation?.offerCode ?? ''),
      dimension29: getLodgeDataByCode(reservation?.lodge?.code || reservation?.property).url, //product scope dimension for resort
      dimension30: 'packages', //product scope dimension for category
      dimension31: numberOfNights, //product scope dimension for number of nights
      dimension32: reservation?.guests?.adults || reservation?.selection?.numAdults, //product scope dimension for number of adults
      dimension33: reservation?.guests?.children?.length || reservation?.selection?.numChildren || 0, //product scope dimension for number of kids
      dimension34: productScope, //product scope dimension for add to cart location (pre vs post transaction)
      dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61: maxOccupancy || null, // product scope dimension for Maximum Suite Occupancy
      dimension62: (reservation?.guests?.children && JSON.stringify(reservation?.guests?.children)) || [], // product scope dimension for Kids Ages
      recommendationVariant: 'other'
    };

    const isRecommendedPackage =
      Object.keys(upsellRecommendationData?.addons || {})?.includes(packageCode) &&
      packagesByCode[packageCode]?.type === 'bundle';
    if (isRecommendedPackage) {
      returnObj.recommendationVariant = upsellRecommendationData?.recommendationVariant ?? 'other';
    }

    if (upsellRecommendationData?.analyticalGroup !== undefined) {
      applyAgentProperty(returnObj, upsellRecommendationData);
    }
    acc.push(returnObj);
    const uniquePackages = [...new Map(acc.map(m => [m.id, m])).values()];
    return uniquePackages;
  }, []);

  if (formatedOffers.length > 0) {
    formatedOffers.forEach(offer => {
      addedPackagesFormat.push({
        name: offer.packageName,
        id: offer.packageCode,
        price: -offer.amount,
        brand: GTM_EVENT.LABEL.greatWolfLowerCase,
        category: offer.category,
        variant: offer.packageCategory,
        quantity: offer.quantity,
        coupon: reservation?.rateCode ?? reservation.offerCode ?? '',
        dimension29: getLodgeDataByCode(reservation?.lodge?.code || reservation.property).url, //product scope dimension for resort
        dimension30: 'packages', //product scope dimension for category
        dimension31: numberOfNights, //product scope dimension for number of nights
        dimension32: reservation?.guests?.adults || reservation.selection.numAdults, //product scope dimension for number of adults
        dimension33: reservation?.guests?.children
          ? reservation.guests.children.length
          : reservation.selection.numChildren, //product scope dimension for number of kids
        dimension34: productScope, //product scope dimension for add to cart location (pre vs post transaction)
        dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
        dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default)
      });
    });

    return addedPackagesFormat;
  }

  return addedPackagesFormat;
};

export const buildImpressionsEventObject = (resortLocation, eventProps, purchaseLocation, promoCode = null) => ({
  event: PRODUCT_SCOPES.productImpressions,
  resortLocation: resortLocation,
  purchaseLocation: purchaseLocation || null,
  coupon: promoCode || null,
  ecommerce: {
    currencyCode: 'USD',
    impressions: Array.isArray(eventProps) ? eventProps : [eventProps]
  }
});

export const buildImpressionsDataObject = (
  kidsAges,
  suite,
  resortLocation,
  checkoutDate,
  checkInDate,
  adultsCount,
  kidsCount,
  currentPosition,
  customPurchaseLocation = null,
  price = null
) => {
  return {
    name: suite.name || suite.title,
    id: suite.code || suite.suiteCode,
    price: price,
    brand: suite.suiteBrand || 'greatwolf',
    category: 'suites',
    variant: suite.suiteCategory,
    list: 'suites',
    position: currentPosition,
    dimension27: suite.suiteFlags && suite.suiteFlags[RECOMMENDED_FOR_YOU] ? 'recommended' : '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: 'suites',
    dimension31: GwDatesWrapper.diff(checkoutDate, checkInDate, 'days'),
    dimension32: adultsCount,
    dimension33: kidsCount,
    dimension34: customPurchaseLocation ? customPurchaseLocation : 'suite-checkout',
    dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    dimension61: Number(suite.maxSleep ?? suite?.selectedSuite?.maxOccupancy) || null, // product scope dimension for Maximum Suite Occupancy
    dimension62: kidsAges || [], // product scope dimension for Kids Ages
    agent: 'GW'
  };
};

export const extraGuestPurchaseObj = (reservation, amount, additionalGuests) => {
  const nights = GwDatesWrapper.diff(reservation?.departure, reservation?.arrival, 'days');
  return {
    name: reservation?.product.name,
    id: reservation?.product.code,
    price: amount.toFixed(2),
    brand: 'great wolf',
    category: 'suites',
    variant: reservation.product.suiteCategory,
    quantity: 1,
    coupon: reservation.offerCode,
    dimension27: 'recommended',
    dimension28: 'recommended',
    dimension29: reservation?.lodge.shortName,
    dimension30: 'suites',
    dimension31: nights,
    dimension32: !additionalGuests.numberOfAdults ? 0 : additionalGuests.numberOfAdults,
    dimension33:
      !additionalGuests.kidsAges || additionalGuests.kidsAges.length === 0 ? 0 : additionalGuests.kidsAges.length,
    dimension34: 'modify reservation-add guest',
    dimension35: GwDatesWrapper.format(reservation?.arrival, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(reservation?.departure, DATE_FORMATS.default),
    dimension61: Number(reservation.product.maxOccupancy), // product scope dimension for Maximum Suite Occupancy
    dimension62: !additionalGuests.kidsAges ? [] : additionalGuests.kidsAges, // product scope dimension for Kids Ages
    dimension63: reservation.payment.description,
    agent: 'GW'
  };
};

const SUITE_UPGRADE_ROOM_CLASS_VARIANTS = { STA: 'standard', THE: 'themed', PRE: 'premium', COM: 'component' };

const getSuiteUpgradeRoomClassVariant = roomClass =>
  SUITE_UPGRADE_ROOM_CLASS_VARIANTS[roomClass] ?? (roomClass ? roomClass.toLowerCase() : '');

const getSuiteUpgradeContext = reservation => ({
  resortLocation: reservation.lodge?.shortName,
  coupon: reservation.rateCode ?? '',
  nights: GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days'),
  adults: reservation.guests?.adults ?? 0,
  kidsAges: reservation.guests?.children ?? []
});

const getSuiteUpgradeDelta = (variant, offerGroup) =>
  variant?.additionalPerNight ?? offerGroup?.additionalPerNight ?? 0;

const buildSuiteUpgradeProduct = (variant, offerGroup, reservation) => {
  const { resortLocation, coupon, nights, adults, kidsAges } = getSuiteUpgradeContext(reservation);
  const content = variant?.content ?? {};
  return {
    name: content.suiteBrand ?? offerGroup?.suiteFamily ?? variant?.roomType ?? '',
    id: content.suiteCode ?? variant?.roomType ?? '',
    price: Number((getSuiteUpgradeDelta(variant, offerGroup) * nights).toFixed(2)),
    brand: GTM_EVENT.LABEL.greatWolfLowerCase,
    category: RESERVATION_TYPE.SUITE,
    variant: getSuiteUpgradeRoomClassVariant(offerGroup?.roomClass),
    dimension27: '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: RESERVATION_TYPE.SUITE,
    dimension31: nights,
    dimension32: adults,
    dimension33: kidsAges.length,
    dimension34: CUSTOM_PURCHASE_LOCATIONS.modifyReservationUpgradeSuite,
    dimension35: GwDatesWrapper.format(reservation.arrival, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(reservation.departure, DATE_FORMATS.default),
    dimension61: variant?.maxOccupancy ?? content.maxOccupancy ?? null,
    dimension62: kidsAges,
    coupon,
    agent: 'GW'
  };
};

/**
 * Build the product-impressions event for the suite upgrade offers shown in the drawer: one
 * impression per offer group, in API order, using the group's representative (first) variant.
 * @param {Array} offerGroups Suite upgrade offer groups as returned by the API.
 * @param {Object} reservation The booked reservation.
 * @returns {Object} The product-impressions event.
 */
export const buildSuiteUpgradeImpressionsEvent = (offerGroups, reservation) => {
  const { resortLocation, nights, adults, kidsAges } = getSuiteUpgradeContext(reservation);
  const impressions = offerGroups.map((offerGroup, index) => {
    const variant = offerGroup?.variants?.[0] ?? {};
    const content = variant.content ?? {};
    const suite = {
      name: content.suiteBrand ?? offerGroup?.suiteFamily ?? variant.roomType,
      suiteCode: content.suiteCode ?? variant.roomType,
      suiteCategory: getSuiteUpgradeRoomClassVariant(offerGroup?.roomClass),
      maxSleep: variant.maxOccupancy ?? content.maxOccupancy
    };
    return buildImpressionsDataObject(
      kidsAges,
      suite,
      resortLocation,
      reservation.departure,
      reservation.arrival,
      adults,
      kidsAges.length,
      index + 1,
      CUSTOM_PURCHASE_LOCATIONS.modifyReservationUpgradeSuite,
      Number((getSuiteUpgradeDelta(variant, offerGroup) * nights).toFixed(2))
    );
  });
  return buildImpressionsEventObject(
    resortLocation,
    impressions,
    CUSTOM_PURCHASE_LOCATIONS.modifyReservationUpgradeSuite
  );
};

export const buildSuiteUpgradeAddToCartEvent = (variant, offerGroup, reservation) => {
  const { resortLocation, coupon } = getSuiteUpgradeContext(reservation);
  return {
    event: EVENT_CATEGORIES.GTM.addToCart,
    resortLocation,
    purchaseLocation: CUSTOM_PURCHASE_LOCATIONS.modifyReservationUpgradeSuite,
    ecommerce: {
      currencyCode: 'USD',
      add: {
        actionField: { list: 'suites', position: '', coupon },
        products: [{ ...buildSuiteUpgradeProduct(variant, offerGroup, reservation), quantity: 1 }]
      }
    }
  };
};

export const buildSuiteUpgradePurchaseEvent = (preview, variant, offerGroup, reservation, uuid, emailHash) => {
  const { resortLocation, coupon } = getSuiteUpgradeContext(reservation);
  const product = {
    ...buildSuiteUpgradeProduct(variant, offerGroup, reservation),
    price: (preview.additionalSuiteCost ?? 0).toFixed(2),
    quantity: 1,
    dimension63: reservation.payment?.description ?? 'Credit Card'
  };
  return {
    event: EVENT_CATEGORIES.GTM.transaction,
    resortLocation,
    purchaseLocation: CUSTOM_PURCHASE_LOCATIONS.modifyReservationUpgradeSuite,
    email_hashed: emailHash,
    ecommerce: {
      currencyCode: 'USD',
      purchase: {
        actionField: {
          id: uuid,
          confirmationNumber: reservation.externalId ?? reservation.id,
          affiliation: GTM_EVENT.LABEL.greatWolfLowerCase,
          revenue: (preview.totalAdditional ?? 0).toFixed(2),
          shipping: '0.00',
          tax: (preview.additionalTax ?? 0).toFixed(2),
          coupon,
          voyagerPointsDiscount: 0,
          offersPointsDiscount: 0,
          SIP: 0,
          agent: 'GW'
        },
        products: [product]
      }
    }
  };
};

export const buildImpressionsDataObjectPkgs = (
  pkg,
  resortLocation,
  checkoutDate,
  checkInDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation,
  customList,
  customD30,
  upsellRecommendationData
) => {
  let returnObj = {
    name: pkg.title ?? pkg.content.title,
    id: pkg.packageCode ?? pkg.content.packageCode,
    price: pkg.amount,
    brand: 'great wolf',
    category: 'package',
    variant: pkg.packageType ? pkg.packageType[0]?.trim() : '',
    list: customList ? customList : 'suites',
    position: currentPosition,
    dimension27: pkg.flags && pkg.flags[RECOMMENDED_FOR_YOU] ? 'recommended' : '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: customD30 ? customD30 : 'suites',
    dimension31: GwDatesWrapper.diff(checkInDate, checkoutDate, 'days'),
    dimension32: adultsCount,
    dimension33: kidsCount,
    dimension34: purchaseLocation || 'suite-checkout',
    dimension35: GwDatesWrapper.format(checkInDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    recommendationVariant:
      upsellRecommendationData?.recommendationVariant && pkg?.packageType?.includes('bundle')
        ? upsellRecommendationData?.recommendationVariant
        : 'other'
  };

  let recommendationData = pkg.analyticalGroup
    ? pkg
    : upsellRecommendationData?.analyticalGroup !== undefined
    ? upsellRecommendationData
    : null;
  if (recommendationData) {
    applyAgentProperty(returnObj, recommendationData);
  }
  return returnObj;
};

export const getDayPassBaseObject = (reservation, locationDisplayName, locationState) => {
  return {
    city: locationDisplayName,
    region: locationState,
    item_code: reservation.reservationType,
    item_group: 'WPF',
    room_type: DAY_PASSES_ROOM_TYPE[reservation.reservationType] || '',
    room_name: reservation.product.name,
    value: reservation.costSummary.total
  };
};

export const getBaseSuiteObject = (reservation, locationDisplayName, locationState, suiteCategory) => {
  const numberOfNights = GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days');
  return {
    checkout_date: GwDatesWrapper.format(reservation.departure, DATE_FORMATS.default),
    num_children: reservation.guests.children.filter(children => children >= 2)?.length,
    num_infants: reservation.guests.children.filter(children => children < 2)?.length,
    children_ages: reservation.guests.children.length,
    city: locationDisplayName,
    region: locationState,
    room_nights: numberOfNights,
    promo_code: reservation.offerCode,
    room_type: suiteCategory,
    room_name: reservation.product.name
  };
};

export const getEventBaseObject = (event, reservation, currencyCode) => {
  const numberOfNights = GwDatesWrapper.diff(reservation.departure, reservation.arrival, 'days');
  const isDayPass = reservation.reservationType.includes('PASS');
  const averageRate = !isDayPass
    ? reservation.product.totalPrice / numberOfNights
    : reservation.product.totalPrice / reservation.guests.quantity;
  return {
    [EVENT_PARAMS.fbEventId]: uuidv4(),
    event,
    transaction_id: reservation.id,
    checkin_date: GwDatesWrapper.format(reservation.arrival, DATE_FORMATS.default),
    num_travelers: reservation.guests.adults + reservation.guests.children.length,
    num_adults: reservation.guests.adults,
    value: reservation.costSummary.total,
    average_room_rate: averageRate.toFixed(2),
    currency: currencyCode,
    country: reservation.payment.billingAddress.country || null
  };
};

export const buildProductClickEnhancedECommerceObject = (
  kidsAges,
  selectedSuite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation
) => {
  return {
    event: PRODUCT_SCOPES.productClick,
    resortLocation: resortLocation,
    ecommerce: {
      currencyCode: 'USD',
      click: {
        actionField: {
          list: 'suites'
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              selectedSuite,
              resortLocation,
              checkoutDate,
              checkinDate,
              adultsCount,
              kidsCount,
              selectedSuite.rate,
              purchaseLocation
            ),
            position: currentPosition
          }
        ]
      }
    }
  };
};

export const buildAddToCartEnhancedEcommerceObject = (
  eventType,
  kidsAges,
  selectedSuite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  currentPosition,
  purchaseLocation,
  upsellRecommendationData
) => {
  return {
    event: eventType,
    resortLocation: resortLocation,
    purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'suites',
          position: currentPosition || '',
          coupon: selectedSuite.rateCode ?? ''
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              selectedSuite,
              resortLocation,
              checkoutDate,
              checkinDate,
              adultsCount,
              kidsCount,
              selectedSuite.totalRate,
              purchaseLocation,
              kidsAges,
              upsellRecommendationData
            ),
            quantity: 1
          }
        ]
      }
    }
  };
};

export const buildSearchObject = (
  resortLocation,
  searchType,
  bonusDealValue,
  searchLocation,
  checkinDate,
  checkoutDate,
  adultsCount,
  kidsCount,
  kidAges,
  discountCode
) => {
  return {
    event: EVENT_CATEGORIES.GTM.search,
    resortLocation: resortLocation,
    searchType: searchType,
    bonus_deal_value: editCopyByReplaceString(COPY.BONUS_DEAL_VALUE, bonusDealValue, '<VALUE>'),
    searchLocation: searchLocation,
    arrival_date: checkinDate,
    departure_date: checkoutDate,
    numberOfAdults: adultsCount,
    numberOfKids: kidsCount,
    kidAges: kidAges,
    discountCode: discountCode,
    NumNights: GwDatesWrapper.diff(checkoutDate, checkinDate, 'days')
  };
};

const buildProductEnhancedECommerce = (
  selectedSuite,
  resortLocation,
  checkoutDate,
  checkinDate,
  adultsCount,
  kidsCount,
  price,
  purchaseLocation,
  kidsAges,
  upsellRecommendationData
) => {
  const nights = GwDatesWrapper.diff(checkoutDate, checkinDate, 'days');
  let returnObj = {
    name: selectedSuite.title,
    id: selectedSuite.suiteCode,
    price: price,
    brand: 'great wolf',
    category: RESERVATION_TYPE.SUITE,
    variant: selectedSuite.suiteCategory,
    dimension27:
      selectedSuite?.suiteFlags && selectedSuite?.suiteFlags.includes(RECOMMENDED_FOR_YOU) ? 'recommended' : '',
    dimension28: '',
    dimension29: resortLocation,
    dimension30: RESERVATION_TYPE.SUITE,
    dimension31: nights,
    dimension32: adultsCount,
    dimension33: kidsCount,
    dimension34: purchaseLocation || 'suite-checkout',
    dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default),
    dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
    dimension61: Number(selectedSuite?.maxOccupancy) || null, // product scope dimension for Maximum Suite Occupancy
    dimension62: kidsAges || [], // product scope dimension for Kids Ages
    coupon: selectedSuite.rateCode ?? ''
  };
  if (upsellRecommendationData?.analyticalGroup !== undefined) {
    applyAgentProperty(returnObj, upsellRecommendationData);
  }
  return returnObj;
};

export const getSignInEventObject = (
  event,
  label,
  sign_in_location = '',
  sign_in_group = '',
  sign_in_military_status = ''
) => {
  return {
    event,
    label,
    // Spec (Adswerve GA4 Voyager) fields for sign-in events
    sign_in_location,
    sign_in_group,
    sign_in_military_status
  };
};

export const getSignUpEventObject = (event, label, isLeadGenSignUp, sign_up_location = '') => {
  if (!event || !label) return;
  const eventObject = {
    event,
    label,
    sign_up_location
  };
  if (isLeadGenSignUp) {
    // Lead-gen sign-up is the automatic Voyager pop-up; spec location value is 'auto'
    return {
      ...eventObject,
      sign_up_location: 'auto'
    };
  } else {
    return eventObject;
  }
};

export const getLeadGenEventObject = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.leadgen,
    action: action,
    label: label
  };
};

export const getPasswordChangeEventObject = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.passwordChange,
    action: action,
    label: label
  };
};

export const getRaFSignUpEventObject = () => {
  if (sessionStorage.getItem(SessionStorageKeys.REFER_A_FRIEND)) {
    const MemberId = getSpecificItem('MemberId', 'referFriend');

    if (typeof MemberId === 'string' && MemberId) {
      const lst = MemberId.slice(-1);
      const action = +lst % 2 === 0 ? GTM_EVENT.NAME.signUpAttemptRaF50 : GTM_EVENT.NAME.signUpAttemptRaF25;
      return getSignUpEventObject(action, GTM_EVENT.LABEL.greatWolf);
    }
  }
};

/**
 * Build enhanced ecommerce object for Checkout steps
 * @param {Object} suite Suite redux object
 * @param {Object} adultsCount Adults count redux object
 * @param {Object} kidsCount Kids count redux object
 * @param {Object} kidsAges Kids ages redux object
 * @param {Object} checkinDateSelection Checkin date selection redux object
 * @param {Object} checkoutDateSelection Checkout date selection redux object
 * @param {Object} resortLocation Resort location redux object
 * @param {number} step Current step at checkout page
 * @param {boolean} isUserLoggedIn User status
 * @param {string} option Payment type
 * @returns Object
 */
export const buildCheckoutEventObject = (
  selectedSuite,
  adultsCount,
  kidsCount,
  kidsAges,
  checkinDateSelection,
  checkoutDateSelection,
  resortLocation,
  step,
  isUserLoggedIn,
  option = ''
) => {
  return {
    event: EVENT_CATEGORIES.GTM.checkoutStep,
    resortLocation: resortLocation,
    loggedinStatus: isUserLoggedIn,
    ecommerce: {
      currencyCode: 'USD',
      checkout: {
        actionField: {
          step,
          option
        },
        products: [
          {
            ...buildProductEnhancedECommerce(
              selectedSuite,
              resortLocation,
              checkoutDateSelection,
              checkinDateSelection,
              adultsCount,
              kidsCount,
              selectedSuite.totalRate,
              undefined,
              kidsAges
            ),
            quantity: 1
          }
        ]
      }
    }
  };
};

/**
 * Build enhanced ecommerce object for add/removal cart
 * @param {string} eventType Event type one of: add-to-cart, remove-from-cart
 * @param {Object} eventPackage Object of the new or removed package
 * @param {Object} packagesTable Packages planPackages redux object -- packagesTableJson
 * @param {Object} guests Guest redux object
 * @param {Object} dates Dates redux object
 * @param {Object} resortLocation Resort location redux object
 * @param qtyDiff
 * @param {string} purchaseLocation Location of the event on the Booking Flow
 * @param currentPathLocation
 * @param suite
 * @param isCartOpenClose
 * @param upsellRecommendationData
 * @param entities
 * @param recommendationVariant
 * @returns Object
 */
export const buildPackageCartEventObject = (
  eventType,
  eventPackage,
  packagesTable,
  guests,
  dates,
  resortLocation,
  qtyDiff = null,
  purchaseLocation = CUSTOM_PURCHASE_LOCATIONS.standardPurchase,
  currentPathLocation = '',
  suite = [],
  isCartOpenClose,
  upsellRecommendationData,
  entities,
  recommendationVariant
) => {
  const { adultsCount, kidsCount, kidsAges } = guests;
  const { checkinDateSelection: checkinDate, checkoutDateSelection: checkoutDate } = dates;
  const numberOfNights = checkoutDate !== undefined && GwDatesWrapper.diff(checkoutDate, checkinDate);

  const coupon =
    entities?.manageReservationDetails?.data?.offerCode ||
    entities?.manageReservationDetails?.data?.rateCode ||
    suite?.selectedSuite?.rateCode ||
    '';

  // Get Products
  const products = eventPackage.map(pkg => {
    let { packageName, packageCode, amount, category, packageCategory, quantity, title, content, packageType } = pkg;
    // Check if is recommended
    const isRecommended = packagesTable && eventPackage ? isRecommendedPackage(eventPackage, packagesTable) : false;
    // Get package type
    packageCategory = packageCategory ? packageCategory : getPackageCategory(pkg, packagesTable)?.toLowerCase();

    // Get quanity -- for dining by package we should to perform one operation
    quantity = quantity
      ? quantity
      : qtyDiff
      ? qtyDiff
      : getPackageQuantity(pkg, packageCategory, guests, numberOfNights);

    category = category ? category : 'packages';

    let returnObj = {
      name: packageName ?? title ?? content?.title,
      id: packageCode,
      price: amount, // the nightly rate. what the user sees on the room card
      brand: 'great wolf', // static
      category: category, // static
      variant: packageCategory, // one of: attractions, family, birthday, dining, lco, flextrip
      quantity: quantity,
      dimension27: '', // empty string
      dimension28: isRecommended ? 'recommended' : '', // if the recommended package, otherwise empty string
      dimension29: resortLocation,
      dimension30: 'packages', // static
      dimension31: numberOfNights, // number of nights
      dimension32: adultsCount, // number of adults
      dimension33: kidsCount, // number of kids
      dimension34: isCartOpenClose
        ? CUSTOM_PURCHASE_LOCATIONS.cart
        : purchaseLocation
        ? purchaseLocation
        : currentPathLocation, // static
      dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default), // arrival date
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61:
        Number(suite?.selectedSuite?.maxOccupancy) ?? entities?.manageReservationDetails?.data?.product.maxOccupancy, // product scope dimension for Maximum Suite Occupancy
      dimension62: kidsAges || [], // product scope dimension for Kids Ages
      coupon,
      recommendationVariant: recommendationVariant && packageType?.includes('bundle') ? recommendationVariant : 'other'
    };
    if (upsellRecommendationData?.analyticalGroup !== undefined) {
      applyAgentProperty(returnObj, upsellRecommendationData);
    }
    return returnObj;
  });

  return {
    event: eventType, // one of: add-to-cart, remove-from-cart
    resortLocation: resortLocation,
    purchaseLocation: isCartOpenClose ? CUSTOM_PURCHASE_LOCATIONS.cart : purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'packages',
          position: '',
          coupon
        },
        products: products
      }
    }
  };
};

export const rateCalendarGA = (action, label) => {
  if (!action || !label) return;
  return {
    event: GTM_EVENT.NAME.websiteVitals,
    category: EVENT_CATEGORIES.GTM.rateCalendar,
    action: action,
    label: label
  };
};

export const OFFERS_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove'
};

export const offerEventBuilder = (action, offerInfo) => {
  return {
    event: `bonus_deal_click_${action}`,
    resortLocation: offerInfo.resortLocation, //custom dimension for resort location that we can easily filter EE events by
    bonus_deal_name: offerInfo.name, //ex. July Bonus,
    bonus_deal_value: offerInfo.value //ex. 50 points,
  };
};

/**
 * Build enhanced ecommerce object for reservation cancellation
 * @param {Object} eventProps Object with relevant data for event
 * @param {Object} resortLocation Resort location redux object
 * @param {string} cancellationData Data related to the cancellation response
 * @param purchaseLocation
 * @returns Object
 */
export const buildCancelReservationEventObject = (
  eventProps,
  resortLocation,
  cancellationData,
  purchaseLocation = CUSTOM_PURCHASE_LOCATIONS.standardPurchase
) => {
  const {
    suiteName,
    suiteCode,
    suiteTotalPrice,
    reservationId,
    adultsCount,
    kidsCount,
    kidsAges,
    rateCode,
    maxOccupancy,
    checkinDate,
    checkoutDate,
    paymentType,
    packages,
    tax,
    resortFee,
    total
  } = eventProps;

  const numberOfNights = checkoutDate !== undefined && GwDatesWrapper.diff(checkoutDate, checkinDate);

  const { totalRefundAmount, cancelNumber, cancellationFeeAmount } = cancellationData;

  const productPackages = packages.map(pkg => ({
    name: pkg.description,
    id: pkg.packageCode,
    price: 0,
    brand: GTM_EVENT.LABEL.greatWolfLowerCase,
    category: 'packages',
    variant: pkg.packageType,
    quantity: pkg.quantity,
    coupon: rateCode,
    dimension31: numberOfNights, //product scope dimension for number of nights
    dimension32: adultsCount, //product scope dimension for number of adults
    dimension33: kidsCount, //product scope dimension for number of kids
    dimension34: PRODUCT_SCOPES.standardPurchase, //product scope dimension for add to cart location (pre vs post transaction)
    dimension35: checkinDate,
    dimension36: checkoutDate,
    dimension63: paymentType,
    dimension68: -pkg.unitAmount
  }));

  const products = [
    {
      name: suiteName,
      id: suiteCode,
      price: suiteTotalPrice / numberOfNights, // the nightly rate. what the user sees on the room card
      brand: 'great wolf', // static
      category: 'suites', // static
      variant: 'standard', // one of: attractions, family, birthday, dining, lco, flextrip
      quantity: 1,
      coupon: rateCode ?? '',
      dimension31: numberOfNights, // number of nights
      dimension32: adultsCount, // number of adults
      dimension33: kidsCount, // number of kids
      dimension34: purchaseLocation, // static
      dimension35: GwDatesWrapper.format(checkinDate, DATE_FORMATS.default), // arrival date
      dimension36: GwDatesWrapper.format(checkoutDate, DATE_FORMATS.default),
      dimension61: maxOccupancy || null, // product scope dimension for Maximum Suite Occupancy
      dimension62: kidsAges || [], // product scope dimension for Kids Ages
      dimension63: paymentType,
      dimension68: -suiteTotalPrice
    },
    ...productPackages
  ];

  const refundAmount = total ? -Number(total) : 0;

  return {
    event: 'refund', // one of: add-to-cart, remove-from-cart
    resortLocation: resortLocation,
    purchaseLocation: purchaseLocation,
    ecommerce: {
      currencyCode: 'USD',
      refund: {
        actionField: {
          id: uuidv4(),
          affiliation: 'great wolf',
          revenue: totalRefundAmount,
          shipping: -resortFee,
          tax: -tax,
          coupon: rateCode,
          voyagerPointsDiscount: 0,
          offersPointsDiscount: 0,
          SIP: 2,
          confirmationNumber: reservationId,
          refundAmount,
          cancellationFee: cancellationFeeAmount,
          cancellationConfimationNumber: cancelNumber
        },
        products: products
      }
    }
  };
};

const voyagersAnalyticsSignInEventDefaultParameters = {
  sign_in_location: 'voyager_booking_flow',
  sign_in_group: '',
  // Spec (Adswerve GA4 Voyager) field name; military_status kept for backward compatibility
  sign_in_military_status: '',
  military_status: ''
};

const getSignInVoyagersEventObject = (eventName, label) => {
  if (!label) return;
  return {
    ...voyagersAnalyticsSignInEventDefaultParameters,
    event: eventName,
    label
  };
};

const getSignUpVoyagersEventObject = (eventName, label) => {
  if (!label) return;
  return {
    sign_up_location: 'voyager_booking_flow',
    event: eventName,
    label
  };
};

export const getSignInAttemptEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInAttempt, label);
export const getSignInSuccessEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInSuccess, label);
export const getSignInFailureEventObjectVoyagers = label =>
  getSignInVoyagersEventObject(GTM_EVENT.NAME.signInFailure, label);
export const getSignUpAttemptEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpAttempt, label);
export const getSignUpSuccessEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpSuccess, label);
export const getSignUpFailureEventObjectVoyagers = label =>
  getSignUpVoyagersEventObject(GTM_EVENT.NAME.signUpFailure, label);

export const LOG_IN_METHODS = {
  SEND_CODE: 'send code',
  PASSWORD: 'login with password',
  GOOGLE: 'login with google'
};

export const getLogInMethodSelectedEventObject = method => ({
  event: 'custom_click',
  click_category: 'otp login',
  click_type: method
});

export const OTP_SIGN_IN_FAILURE_REASONS = {
  CODE_INCORRECT: 'code incorrect',
  LIMIT_REACHED: 'limit reached',
  EXPIRED: 'expired'
};

// Maps a rejected OTP check (400/429 error or a 2xx with isValid false) to the reason reported to GA
export const getOtpSignInFailureReason = ({ status, errorCode, isValid } = {}) => {
  if (errorCode === 'AUTH031') return OTP_SIGN_IN_FAILURE_REASONS.CODE_INCORRECT;
  if (errorCode === 'AUTH033') return OTP_SIGN_IN_FAILURE_REASONS.EXPIRED;
  if (status === 429 || errorCode === 'AUTH026') return OTP_SIGN_IN_FAILURE_REASONS.LIMIT_REACHED;
  if (errorCode === 'AUTH030' || isValid === false) return OTP_SIGN_IN_FAILURE_REASONS.CODE_INCORRECT;
  return errorCode || 'unknown';
};

export const OTP_ABANDON_ACTIONS = {
  CHANGE_EMAIL: 'change email',
  TRY_ANOTHER_WAY: 'try another way',
  CLOSE: 'close'
};

export const getOtpAbandonEventObject = action => ({
  event: 'custom_click',
  click_category: 'otp abandon',
  click_type: action
});

export const getOtpResendCodeEventObject = () => ({
  event: 'custom_click',
  click_category: 'otp',
  click_type: 'resend code'
});

export const getBirthdayPartySearchObject = (
  resortLocation,
  searchLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators
) => {
  return {
    event: EVENT_CATEGORIES.GTM.search,
    resortLocation,
    searchType: 'birthday',
    bonus_deal_value: '',
    searchLocation: `${searchLocation} - birthday party`,
    arrival_date: partyDate,
    departure_date: '',
    numberOfAdults: partyAdults,
    numberOfKids: partyKids,
    numberOfInfants: partyInfants,
    numberOfSpectators: partySpectators,
    kidAges: [],
    discountCode: '',
    NumNights: '',
    party_time: ''
  };
};

const getBirthdayPartyProductObject = (
  resortLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators,
  partyTime,
  productTitle,
  productId,
  productPrice
) => {
  return {
    name: productTitle,
    id: productId,
    price: productPrice,
    brand: 'great wolf',
    category: 'birthday party',
    variant: 'birthday party',
    quantity: 1,
    dimension27: '',
    dimension28: '',
    dimension29: resortLocation, //product scope dimension for resort
    dimension30: 'packages', //product scope dimension for item category
    dimension31: 1, //product scope dimension for number of nights
    dimension32: partyAdults, //product scope dimension for number of adults
    dimension33: partyKids, //product scope dimension for number of kids
    dimension34: CUSTOM_PURCHASE_LOCATIONS.birthdayParty, //product scope dimension for add location
    dimension35: partyDate, //party date
    dimension36: '',
    dimension61: '', //product scope dimension for max occupancy
    dimension62: [], //product scope dimension for kids ages
    coupon: '',
    dimension64: partyInfants, // product scope dimension for number of infants
    dimension65: partySpectators, // product scope dimension for number of spectators
    dimension67: partyTime // product scope dimension for party time"
  };
};

export const getBirthdayPartySelectObject = (
  eventType,
  resortLocation,
  partyDate,
  partyAdults,
  partyKids,
  partyInfants,
  partySpectators,
  partyTime,
  productTitle,
  productId,
  productPrice
) => {
  return {
    event: eventType,
    resortLocation,
    purchaseLocation: CUSTOM_PURCHASE_LOCATIONS.birthdayParty,
    ecommerce: {
      currencyCode: 'USD',
      [eventType === EVENT_CATEGORIES.GTM.addToCart ? 'add' : 'remove']: {
        actionField: {
          list: 'packages',
          position: '',
          coupon: ''
        },
        products: [
          getBirthdayPartyProductObject(
            resortLocation,
            partyDate,
            partyAdults,
            partyKids,
            partyInfants,
            partySpectators,
            partyTime,
            productTitle,
            productId,
            productPrice
          )
        ]
      }
    }
  };
};

export const getPromoSearchCustomView = recommendedPromo => ({
  event: EVENT_CATEGORIES.GTM.customView,
  viewCategory: 'promo error',
  viewType: recommendedPromo
});

export const getPromoErrorEventObject = (
  resortLocation,
  checkinDate,
  checkoutDate,
  numberOfAdults,
  kidsAges,
  recommendedPromo
) => ({
  event: EVENT_CATEGORIES.GTM.search,
  resortLocation,
  searchType: 'promo code error',
  bonus_deal_value: '',
  searchLocation: CUSTOM_PURCHASE_LOCATIONS.standardPurchase,
  arrival_date: checkinDate,
  departure_date: checkoutDate,
  numberOfAdults,
  numberOfKids: kidsAges?.length ?? 0,
  numberOfInfants: '',
  numberOfSpectators: '',
  kidsAges,
  discountCode: recommendedPromo,
  NumNights: GwDatesWrapper.diff(checkoutDate, checkinDate, 'days'),
  party_time: ''
});
